<template>
  <div class="q-gutter-y-lg">
    <p>
      {{
        $t("external_item_form.description", {
          platformName: appConfig.platformName,
        })
      }}
    </p>
    <BaseInputText
      v-model="externalItem.title"
      :aria-label="$t('external_item_form.add_title_aria')"
      class="input"
      new-design
      :placeholder="$t('external_item_form.add_title')"
      :validations="titleValidations"
    >
      <template #label>
        <div class="field-heading q-mb-sm">{{ $t("global.title") }}</div>
      </template>

      <template #textboxAppend="{ focused }">
        <CharacterCounter
          v-if="focused || externalItem.title.length > titleMaxLength"
          :characters="externalItem.title.length"
          :limit="titleMaxLength"
        />
      </template>
    </BaseInputText>

    <BaseInputText
      v-model="externalItem.url"
      :aria-label="$t('external_item_form.add_url_aria')"
      class="input"
      data-testid="external-item-url-input"
      :disabled="disabledFields.includes('url')"
      new-design
      :placeholder="$t('external_item_form.add_url')"
      :validations="['notBlank', 'isUrl']"
    >
      <template #label>
        <div class="field-heading q-mb-xs">
          {{ $t("external_item_form.url") }}
        </div>
        <div class="field-subtext q-mb-sm">
          {{ $t("external_item_form.copy_paste") }}
        </div>
      </template>
    </BaseInputText>

    <div class="row q-col-gutter-x-md">
      <div class="relative col-6">
        <div class="field-heading q-mb-sm">
          {{ $t("external_item_form.publication_date") }}
        </div>
        <BaseInputDatePicker
          v-model="publishedAt"
          bordered
          class="q-mt-xs q-mb-lg full-width"
          close-on-update
          date-style="short"
          :max-date="new Date()"
          :placeholder="$t('global.date')"
          show-date-input
          show-time-input
          show-update-button
          time-style="short"
        />
      </div>

      <div class="col-6">
        <TimezoneDropdown
          v-model="externalItem.timeZone"
          clearable
          new-design
          :validations="['notBlank']"
        />
      </div>
    </div>

    <div class="row q-col-gutter-x-md">
      <div class="col-6">
        <div class="field-heading q-mb-sm">
          {{ $t("external_item_form.mention_streams") }}
        </div>
        <InputSelectWithCheckboxes
          v-model="handleSelectedStreamIds"
          :aria-label="$t('external_item_form.mention_streams')"
          checkbox-color="primary-streem"
          :disabled="disabledFields.includes('streams')"
          emit-value
          grouped-options
          :min-selected="1"
          :options="streamOptions"
          :placeholder-text="$t('external_item_form.select_mention_streams')"
          searchable
          :selected-text="mentionStreamsSelectedText"
          show-icon
        />
      </div>

      <div class="col-6">
        <div class="field-heading q-mb-sm">
          {{ $t("external_item_form.media_type") }}
        </div>

        <BaseInputSelect
          v-model="externalItem.medium"
          :aria-label="$t('external_item_form.media_type')"
          emit-value
          new-design
          option-label="label"
          option-prop="value"
          :options="selectedMedia"
          :placeholder="$t('external_item_form.select_media_type')"
          :validations="['notBlank']"
        />
      </div>
    </div>

    <div class="row q-col-gutter-x-md">
      <div class="col-6">
        <BaseInputText
          v-model="externalItem.sourceName"
          :aria-label="$t('external_item_form.add_source_aria')"
          class="input"
          new-design
          :placeholder="$t('external_item_form.add_source')"
          :validations="sourceValidations"
        >
          <template #label>
            <div class="field-heading q-mb-sm">
              {{ $t("external_item_form.source") }}
            </div>
          </template>

          <template #textboxAppend="{ focused }">
            <CharacterCounter
              v-if="focused || sourceNameLength > sourceNameMaxLength"
              :characters="sourceNameLength"
              :limit="sourceNameMaxLength"
            />
          </template>
        </BaseInputText>
      </div>

      <div class="col-6">
        <BaseInputText
          v-model="audience"
          :aria-label="$t('external_item_form.enter_amount_aria')"
          new-design
          :placeholder="$t('external_item_form.enter_amount')"
          type="number"
          :validations="audienceValidations"
        >
          <template #label>
            <div class="field-heading q-mb-sm">
              {{ $t("external_item_form.potential_audience") }}
              <QIcon
                class="q-ml-xs"
                name="ion-information-circle"
              >
                <BaseStreemTooltip>
                  <i18n-t
                    keypath="external_item_form.potential_audience_tooltip"
                    scope="global"
                  >
                    <template #lineBreak><br /></template>
                  </i18n-t>
                </BaseStreemTooltip>
              </QIcon>
            </div>
          </template>
        </BaseInputText>
      </div>
    </div>

    <div class="row q-col-gutter-x-md">
      <div class="col-6">
        <BaseInputText
          v-model="advertisementRate"
          :aria-label="$t('external_item_form.enter_advertisement_rate_aria')"
          new-design
          :placeholder="$t('external_item_form.enter_amount')"
          type="number"
          :validations="advertisementRateValidations"
        >
          <template #label>
            <div class="field-heading q-mb-sm">
              {{ $t("external_item_form.ave") }}
              <QIcon
                class="q-ml-xs"
                name="ion-information-circle"
              >
                <BaseStreemTooltip>
                  {{ $t("external_item_form.ave_tooltip") }}
                </BaseStreemTooltip>
              </QIcon>
            </div>
          </template>
        </BaseInputText>
      </div>

      <div class="col-6">
        <BaseInputSelect
          v-model="externalItem.sentiment"
          :aria-label="$t('external_item_form.sentiment_label')"
          emit-value
          new-design
          option-label="label"
          option-prop="value"
          :options="selectedSentiment"
          :placeholder="$t('external_item_form.select_sentiment')"
        >
          <template #label>
            <div class="field-heading q-mb-sm">
              {{ $t("external_item_form.sentiment") }}
              <QIcon
                class="q-ml-xs"
                name="ion-information-circle"
              >
                <BaseStreemTooltip>
                  {{ $t("external_item_form.sentiment_tooltip") }}
                </BaseStreemTooltip>
              </QIcon>
            </div>
          </template>
        </BaseInputSelect>
      </div>
    </div>

    <BaseInputText
      v-model="externalItem.summary"
      :aria-label="$t('external_item_form.summary_aria')"
      :height="116"
      new-design
      type="textarea"
      :validations="summaryValidations"
    >
      <template #label>
        <div class="field-heading q-mb-sm">
          {{ $t("external_item_form.summary") }}
        </div>
      </template>

      <template #textboxAppend="{ focused }">
        <CharacterCounter
          v-if="focused || externalItem.summary.length > summaryMaxLength"
          :characters="externalItem.summary.length"
          :limit="summaryMaxLength"
        />
      </template>
    </BaseInputText>
    <div
      class="row"
      style="min-height: 160px"
    >
      <div class="col-1">
        <BaseCheckbox
          v-model="handleAcceptConditions"
          align-top
          new-design
        />
      </div>
      <div class="q-pl-none col-11">
        <div class="text-help-text">
          {{
            $t("external_item_form.terms", {
              organisationName: organisation.name,
              platformName: appConfig.platformName,
            })
          }}
        </div>
        <div
          v-if="!acceptConditions"
          class="field-error q-mt-sm"
        >
          {{ $t("external_item_form.declaration_must_be_checked") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import {
  BaseInputDatePicker,
  BaseInputSelect,
  BaseInputText,
  BaseStreemTooltip,
} from "shared/components/base";
import CharacterCounter from "shared/components/CharacterCounter.vue";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import InputSelectWithCheckboxes from "shared/components/core/forms/InputSelectWithCheckboxes.vue";
import { sentimentOptions } from "shared/components/core/pickers/SentimentPicker";
import TimezoneDropdown from "shared/components/core/pickers/TimezoneDropdown";
import { maximumLength, maximumNumber } from "shared/composables/useValidation";
import { getSources } from "shared/helpers/media";
import { useGlobalStore } from "shared/stores/global";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";

const INTEGER_MAX_LIMIT = 2_147_483_647;

export default {
  name: "ExternalItemForm",
  components: {
    CharacterCounter,
    BaseCheckbox,
    BaseInputDatePicker,
    BaseInputSelect,
    InputSelectWithCheckboxes,
    BaseInputText,
    BaseStreemTooltip,
    TimezoneDropdown,
  },
  props: {
    externalItem: {
      type: Object,
      required: true,
    },
    acceptConditions: {
      type: Boolean,
    },
    selectedStreamIds: {
      type: Array,
      default: () => [],
    },
    disabledFields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:acceptConditions", "update:selectedStreamIds"],
  setup() {
    const streamsStore = useStreamsStore();

    const { groupedTraditionalStreams } = streamsStore;

    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    return {
      groupedTraditionalStreams,
      organisation,
      appConfig,
    };
  },
  data() {
    return {
      titleMaxLength: 255,
      sourceNameMaxLength: 255,
      summaryMaxLength: 300,
    };
  },
  computed: {
    titleValidations() {
      return [maximumLength(this.titleMaxLength), "notBlank"];
    },
    sourceNameLength() {
      if (!this.externalItem.sourceName) return 0;

      return this.externalItem.sourceName.length;
    },
    sourceValidations() {
      return [maximumLength(this.sourceNameMaxLength)];
    },
    audienceValidations() {
      return ["isNumber", this.validateMaximumAudienceNumber];
    },
    advertisementRateValidations() {
      return ["isNumber", this.validateMaximumAdvertisementRateNumber];
    },
    audience: {
      get() {
        return this.externalItem.audience;
      },
      set(value) {
        this.externalItem.audience = Number(value) || null;
      },
    },
    advertisementRate: {
      get() {
        return Number(this.externalItem.advertisementRate) / 100 || null;
      },
      set(value) {
        this.externalItem.advertisementRate = Number(value) * 100 || null;
      },
    },
    summaryValidations() {
      return [maximumLength(300)];
    },
    streamOptions() {
      return this.groupedTraditionalStreams().map((group) => ({
        value: group.id,
        label: group.label,
        color: group.color,
        isGroup: true,
        options: group.streams.map((stream) => ({
          value: stream.id,
          label: stream.label,
          color: stream.color || group.color,
        })),
      }));
    },
    selectedMedia() {
      return getSources().map((media) => ({
        label: media.label,
        value: media.medium,
      }));
    },
    selectedSentiment() {
      return [
        {
          value: null,
          label: "No Rating",
          field: "none",
          color: "#bfbfbf",
          icon: "fas fa-meh-blank",
          inRange: () => false,
        },
      ].concat(sentimentOptions);
    },
    mentionStreamsSelectedText() {
      return (count) =>
        this.$t("external_item_form.stream_selected", { count });
    },
    publishedAt: {
      get() {
        return new Date(this.externalItem.publishedAt);
      },
      set(value) {
        this.externalItem.publishedAt = value;
      },
    },
    handleAcceptConditions: {
      get() {
        return this.acceptConditions;
      },
      set(value) {
        this.$emit("update:acceptConditions", value);
      },
    },
    handleSelectedStreamIds: {
      get() {
        return this.selectedStreamIds;
      },
      set(value) {
        this.$emit("update:selectedStreamIds", value);
      },
    },
  },
  methods: {
    validateMaximumAudienceNumber() {
      const result = maximumNumber(INTEGER_MAX_LIMIT)(
        this.externalItem.audience
      );

      if (result !== true) {
        return this.$t("external_item_form.validate_maximum_audience_number");
      }

      return null;
    },
    validateMaximumAdvertisementRateNumber() {
      if (!this.externalItem.advertisementRate) return null;

      const result = maximumNumber(INTEGER_MAX_LIMIT)(
        this.externalItem.advertisementRate
      );

      if (result !== true) {
        return this.$t("external_item_form.validate_maximum_ave_number");
      }

      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.picker-hide {
  visibility: hidden;
  position: absolute;
  top: 48px;
  height: 0;
}
</style>
